import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { PatientHistoryType } from 'src/graphql.generated'
import { useAdvancedOverviewState } from '../../api/AdvancedOverviewProvider'
import { PatientHistoryEntry, useAdvancedPatientHistory } from '../../api/useAdvancedPatientHistory'
import {
  ActivityContext,
  ActivityFacts,
  ChatMessageLink,
  ConversationItem,
  ConversationPaging,
  isUnread,
  usePatientConversation,
} from '../../api/patientConversation'
import { toIso } from '../../format'
import { useLastSeenAt } from './PatientFeedProvider'
import { MessageAttachment } from './types'
import { useCareTeamRoster } from './useCareTeamRoster'
import { useHideSharedChat } from './useHideSharedChat'

export type ConversationActivityType =
  | 'lessonResponse'
  | 'contentCompletion'
  | 'reflection'
  | 'videoCall'
  | 'labOrder'
  | 'overdueQuestionnaire'

export type FeedEvent =
  | { kind: 'history'; id: string; timestamp: string; entry: PatientHistoryEntry; category: 'system'; unread: false }
  | {
      kind: 'chat'
      id: string
      timestamp: string
      fromPatient: boolean
      authorCoachId: string | null
      text: string
      channel: 'inApp' | 'sms'
      attachments: MessageAttachment[]
      linkedTo: ChatMessageLink | null
      category: string
      unread: boolean
    }
  | {
      kind: 'activity'
      id: string
      timestamp: string
      label: string
      detail: string | null
      activityType: ConversationActivityType
      responseID: string | null
      category: string
      context: ActivityContext | null
      facts: ActivityFacts | null
      iconCategory: string
      continued: boolean
      isCompleted: boolean
      unread: boolean
    }

// Luxon, like the day dividers: `Date` reads a date-only history `date` as UTC midnight.
const sortKey = (event: FeedEvent) => DateTime.fromISO(event.timestamp).toMillis() || 0

const conversationEvent = (
  item: ConversationItem,
  categoryFor: (coachId: string) => string | null,
  lastSeenAt: string | null | undefined
): FeedEvent => {
  const unread = isUnread(item, lastSeenAt)

  if (item.kind === 'activity') {
    return {
      kind: 'activity',
      id: item.id,
      timestamp: item.createdAt,
      label: item.label,
      detail: null,
      activityType: item.activityType,
      responseID: item.responseID,
      category: (item.coachID && categoryFor(item.coachID)) || 'system',
      context: item.context,
      facts: item.facts,
      iconCategory: item.iconCategory,
      continued: item.continued,
      isCompleted: item.isCompleted,
      unread,
    }
  }
  const fromPatient = !item.coachID

  return {
    kind: 'chat',
    id: item.id,
    timestamp: item.createdAt,
    fromPatient,
    authorCoachId: item.coachID,
    text: item.text,
    channel: item.kind === 'sms' ? 'sms' : 'inApp',
    attachments: item.kind === 'chat' ? item.files : [],
    linkedTo: item.kind === 'chat' ? item.linkedTo : null,
    category: fromPatient ? 'patient' : (item.coachID && categoryFor(item.coachID)) || 'system',
    unread,
  }
}

export type ChatEventFeed = ConversationPaging & { events: FeedEvent[] }

export const useChatEventFeed = (uid: string): ChatEventFeed => {
  const history = useAdvancedPatientHistory(uid)
  const hideSharedChat = useHideSharedChat()
  const { items, hasOlder, isFetchingOlder, fetchMoreOlder, fetchMoreNewer } = usePatientConversation(uid, {
    hideSharedChat,
  })
  const { categoryFor } = useCareTeamRoster(uid)
  const overview = useAdvancedOverviewState()
  const lastSeenAt = useLastSeenAt()

  const events = useMemo(() => {
    const completions = overview.status === 'ready' ? overview.overview.completions : []
    const reflections = overview.status === 'ready' ? overview.overview.reflections : []

    // Standard's feed shows the order's status cards, not its results note; Participant history keeps the note.
    const historyEvents: FeedEvent[] = history
      .filter((entry) => entry.type !== PatientHistoryType.LabOrder)
      .map((entry) => ({
        kind: 'history',
        id: entry.entryID,
        timestamp: entry.date ?? toIso(entry.createdAt) ?? '',
        entry,
        category: 'system',
        unread: false,
      }))

    const chatEvents = items.map((item) => conversationEvent(item, categoryFor, lastSeenAt))

    const completionEvents: FeedEvent[] = completions.map((completion) => ({
      kind: 'activity',
      id: `completion:${completion.completionId}`,
      timestamp: toIso(completion.completedAt) ?? '',
      label: completion.title,
      detail: null,
      activityType: 'contentCompletion',
      responseID: null,
      category: completion.trackKey,
      context: null,
      facts: null,
      iconCategory: 'DEFAULT',
      continued: false,
      isCompleted: true,
      unread: false,
    }))

    const reflectionEvents: FeedEvent[] = reflections.map((reflection) => ({
      kind: 'activity',
      id: `reflection:${reflection.reflectionId}`,
      timestamp: toIso(reflection.createdAt) ?? '',
      label: reflection.prompt,
      detail: reflection.body,
      activityType: 'reflection',
      responseID: null,
      category: reflection.trackKey,
      context: reflection.title ? { title: reflection.title, summary: null, programID: null, sections: [] } : null,
      facts: null,
      iconCategory: 'PERSONAL_REFLECTION',
      continued: false,
      isCompleted: true,
      unread: false,
    }))

    // Oldest first, as Standard's feed cache policy orders `items` whatever the query's `order`.
    const merged = [...historyEvents, ...chatEvents, ...completionEvents, ...reflectionEvents].sort(
      (a, b) => sortKey(a) - sortKey(b)
    )

    // Older than the loaded page, they would hide a gap and sit above "See more"; it reveals them.
    return hasOlder && chatEvents.length > 0
      ? merged.filter((event) => sortKey(event) >= sortKey(chatEvents[0]))
      : merged
  }, [history, items, hasOlder, categoryFor, overview, lastSeenAt])

  return { events, hasOlder, isFetchingOlder, fetchMoreOlder, fetchMoreNewer }
}
